import React, { useCallback, useEffect, useRef } from "react";
import {
  Link,
  useHistory,
  useLocation,
  match as Match,
} from "react-router-dom";
import classnames from "classnames";
import debounce from "lodash/debounce";

import { useResizeObserver } from "../../hooks/useResizeObserver";
import { handleKeyDown, handleMouseDown } from "../../helpers/buttonHandlers";

import styles from "./Tabs.module.scss";

export interface Tab {
  id: number;
  label: string;
  url?: string;
  isDisabled?: boolean;
}

export interface Props {
  items: Tab[];
  activeTabId?: number;
  onChange?: (id: number) => void;
  className?: string;
  isNavigation?: boolean;
  match?: Match;
}

const defaultProps = {
  items: [],
  onChange: () => {},
};

const Tabs = (props: Props) => {
  const {
    match,
    activeTabId,
    className,
    isNavigation,
    items = defaultProps.items,
    onChange = defaultProps.onChange,
  } = props;
  const tabsRef = useRef(null);
  const history = useHistory();
  const { pathname } = useLocation();
  const { url, isExact } = match || {};
  const markerRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null);
  const urlTail = pathname.slice(url?.length);
  const firstItemUrl = items[0]?.url;

  const changeMarkerProperties = () => {
    if (activeItemRef.current && markerRef.current) {
      const { offsetLeft: left, offsetWidth: width } = activeItemRef.current;
      const marker = markerRef.current.style;

      marker.left = `${left}px`;
      marker.width = `${width}px`;
    }
  };

  // eslint-disable-next-line
  const changeMarkerPropertiesDebounced = useCallback(
    debounce(changeMarkerProperties, 100),
    []
  );

  useResizeObserver(tabsRef, changeMarkerPropertiesDebounced);

  useEffect(() => {
    return () => {
      changeMarkerPropertiesDebounced.cancel();
    };
  }, [changeMarkerPropertiesDebounced]);

  useEffect(() => {
    if (isExact && isNavigation && firstItemUrl) {
      history.replace(`${url}${firstItemUrl}`);
    }
  }, [history, isExact, isNavigation, url, firstItemUrl]);

  useEffect(() => {
    changeMarkerPropertiesDebounced();
  }, [activeTabId, changeMarkerPropertiesDebounced]);

  useEffect(() => {
    if (isNavigation) {
      const tabId = items.find((i) => urlTail.startsWith(i.url!))?.id;

      if (tabId && tabId !== activeTabId) {
        onChange(tabId);
      }
    }
  }, [isNavigation, items, urlTail]); // eslint-disable-line

  const isTabActive = (item: Tab) =>
    isNavigation && item.url
      ? urlTail.startsWith(item.url)
      : activeTabId === item.id;

  const getRefForActiveTab = (item: Tab) => {
    if (isTabActive(item)) {
      return activeItemRef;
    }
    return null;
  };

  return (
    <div ref={tabsRef} className={classnames(styles.etTabs, className)}>
      {items.map((item) => {
        const onClick = () => onChange?.(item.id);
        const commonProps = {
          onMouseDown: handleMouseDown,
          tabIndex: item.isDisabled ? -1 : 0,
          className: classnames(styles.etTabLink, {
            [styles.etTabLinkActive]: isTabActive(item),
            [styles.etTabLinkDisabled]: item.isDisabled,
          }),
        };

        return (
          <div
            key={item.id}
            className={styles.etTab}
            ref={getRefForActiveTab(item)}
          >
            {isNavigation && item.url ? (
              <Link to={`${url}${item.url}`} {...commonProps}>
                {item.label}
              </Link>
            ) : (
              <div // eslint-disable-line
                role="button"
                onClick={() => onChange?.(item.id)}
                onKeyDown={(e) => handleKeyDown(e, onClick)}
                {...commonProps}
              >
                {item.label}
              </div>
            )}
          </div>
        );
      })}
      <div ref={markerRef} className={styles.etTabsMarker} />
    </div>
  );
};

export default Tabs;
